import { useState, useEffect, useCallback, memo } from "react";
import AnimacionCarga from "../components/animacion_de_carga";
import { obtenerClientes } from "../../functions/consultas";
import { actualizarRegistro } from "../../functions/crud";
import { ZONAS_CATALOGO } from "../../functions/zonas_catalogo";
import { crearZona, ZONA_POR_DEFECTO } from "../../functions/reservas";
import Swal from "sweetalert2";
import BarraSuperior from "./barra_superior";
import BotonSOS from "./sos/boton_sos";

const MODULOS_APP = [
  { key: "moduloVisitantes",    label: "Visitantes",      icon: "👥", desc: "Control de ingreso de visitantes" },
  { key: "moduloEncomiendas",   label: "Encomiendas",     icon: "📦", desc: "Gestión de paquetes y domicilios" },
  { key: "moduloReservas",      label: "Reservas",        icon: "📅", desc: "Reserva de zonas sociales" },
  { key: "moduloPagos",         label: "Pagos",           icon: "💳", desc: "Pagos de administración" },
  { key: "moduloPqrs",          label: "PQRS",            icon: "📝", desc: "Peticiones, quejas y reclamos" },
  { key: "moduloAnuncios",      label: "Anuncios",        icon: "📢", desc: "Circulares y comunicados" },
  { key: "moduloPanico",        label: "Pánico",          icon: "🚨", desc: "Botón de emergencia SOS" },
  { key: "moduloHogar",         label: "Hogar",           icon: "🏠", desc: "Control de dispositivos del hogar" },
  { key: "moduloSaldoCuenta",   label: "Saldo cartera",   icon: "💰", desc: "Consulta de saldo y cartera" },
  { key: "moduloParqueadero",   label: "Cobro parqueadero", icon: "🅿️", desc: "Tarifas y factura de parqueadero de visitantes" },
];

// Mismo catálogo de la app (functions/zonas_catalogo.js), incluida "Mudanzas".
const ZONAS = ZONAS_CATALOGO.map(z => ({ key: z.campo, id: z.id, label: z.titulo, icon: z.icono }));

const Toggle = ({ checked, onChange }) => (
  <button type="button" onClick={() => onChange(!checked)}
    style={{ ...s.toggleBtn, background: checked ? "#e53e3e" : "#d1d5db" }}>
    <div style={{ ...s.toggleKnob, transform: checked ? "translateX(20px)" : "translateX(2px)" }} />
  </button>
);

const ConfiguracionModulos = memo(() => {
  const [cliente,   setCliente]   = useState(null);
  const [cargando,  setCargando]  = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [modulos,   setModulos]   = useState({});
  const [zonas,     setZonas]     = useState({});
  const [pantalla,  setPantalla]  = useState("NOTICIAS");

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const data = await obtenerClientes();
      const nit  = localStorage.getItem("CodigoCliente");
      const entry = Object.values(data).find(c => String(c.Nit) === String(nit) || String(c.codigo) === String(nit));
      if (!entry) { setCargando(false); return; }

      setCliente(entry);
      setPantalla(entry.moduloPrincipal || "NOTICIAS");

      const initMod = {};
      MODULOS_APP.forEach(m => {
        // Igual que la app: saldo cartera apagado por defecto, el resto encendido.
        initMod[m.key] = entry[m.key] ?? m.key !== "moduloSaldoCuenta";
      });
      setModulos(initMod);

      const initZon = {};
      ZONAS.forEach(z => {
        // Igual que ClienteConjuntoModel.getZona: sin valor = apagada.
        initZon[z.key] = entry[z.key] ?? false;
      });
      setZonas(initZon);
    } catch (e) {
      console.error(e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const toggleModulo = (key) => setModulos(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleZona   = (key) => setZonas(prev   => ({ ...prev, [key]: !prev[key] }));

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const nit = localStorage.getItem("CodigoCliente");
      const id  = cliente.id;

      const payload = {
        ...cliente,
        ...modulos,
        ...zonas,
        moduloPrincipal: pantalla,
        usuario: localStorage.getItem("NombreCompleto") || "",
        fecha: new Date().toISOString(),
      };

      await actualizarRegistro(`coordi_ph_clientes/${nit}/${id}`, payload);

      // Igual que habilitar una zona en la app (seleccionar_zonas.dart): la zona recién
      // encendida recibe las reglas por defecto en coordi_ph_clientes/{codigo}/{zonaId}.
      const nuevas = ZONAS.filter(z => zonas[z.key] === true && cliente[z.key] !== true);
      await Promise.all(nuevas.map(z => crearZona(nit, z.id, ZONA_POR_DEFECTO)));
      setCliente(payload);

      Swal.fire({ title: "¡Guardado!", text: "Configuración actualizada.", icon: "success", timer: 2000, showConfirmButton: false });
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo guardar: " + err.message, "error");
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) return <AnimacionCarga />;
  if (!cliente) return <div style={s.empty}>No se encontró información del conjunto.</div>;

  return (
    <div style={s.page}>

      <BarraSuperior></BarraSuperior>

      <br></br>
      <br></br>

      <form onSubmit={guardar} style={s.form}>

        <div style={s.section}>
          <h3 style={s.sectionTitle}>🏠 Pantalla de inicio</h3>
          <div style={s.sectionBody}>
            <p style={s.hint}>Define qué pantalla verán los residentes al abrir la app.</p>
            <div style={s.pantallaGrid}>
              {["NOTICIAS", "SOS"].map(op => (
                <button
                  key={op} type="button"
                  onClick={() => setPantalla(op)}
                  style={{
                    ...s.pantallaBtn,
                    ...(pantalla === op ? s.pantallaBtnActive : {}),
                  }}
                >
                  <span style={{ fontSize: 28 }}>{op === "NOTICIAS" ? "📰" : "🚨"}</span>
                  <span style={s.pantallaBtnLabel}>{op === "NOTICIAS" ? "Noticias" : "SOS / Pánico"}</span>
                  {pantalla === op && <span style={s.pantallaBtnCheck}>✓</span>}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={s.section}>
          <h3 style={s.sectionTitle}>📱 Módulos de la aplicación</h3>
          <div style={s.sectionBody}>
            <p style={s.hint}>Activa o desactiva las secciones que verán los residentes.</p>
            <div style={s.modulosGrid}>
              {MODULOS_APP.map(m => (
                <div key={m.key}
                  style={{ ...s.moduloCard, ...(modulos[m.key] ? s.moduloCardActive : {}) }}
                  onClick={() => toggleModulo(m.key)}
                >
                  <div style={s.moduloIcon}>{m.icon}</div>
                  <div style={s.moduloInfo}>
                    <span style={s.moduloLabel}>{m.label}</span>
                    <span style={s.moduloDesc}>{m.desc}</span>
                  </div>
                  <Toggle checked={!!modulos[m.key]} onChange={() => toggleModulo(m.key)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={s.section}>
          <h3 style={s.sectionTitle}>🏊 Zonas sociales disponibles</h3>
          <div style={s.sectionBody}>
            <p style={s.hint}>Selecciona las zonas sociales habilitadas para reservas.</p>
            <div style={s.zonasGrid}>
              {ZONAS.map(z => (
                <button
                  key={z.key} type="button"
                  onClick={() => toggleZona(z.key)}
                  style={{
                    ...s.zonaBtn,
                    ...(zonas[z.key] ? s.zonaBtnActive : {}),
                  }}
                >
                  <span style={{ fontSize: 22 }}>{z.icon}</span>
                  <span style={s.zonaBtnLabel}>{z.label}</span>
                  {zonas[z.key] && <div style={s.zonaCheck}>✓</div>}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={s.meta}>
          <span>Último editor: <b>{cliente.usuario || "—"}</b></span>
          <span>Actualización: <b>{cliente.fecha || "—"}</b></span>
        </div>

        <button type="submit" disabled={guardando}
          style={{ ...s.btn, opacity: guardando ? 0.7 : 1 }}>
          {guardando ? "Guardando…" : "💾 Guardar configuración"}
        </button>

      </form>
      <BotonSOS />
    </div>
  );
});

const s = {
  page: {
    minHeight: "100vh",
    background: "#ffffff",
    fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
    paddingBottom: 60,
  },
  form: { maxWidth: 960, margin: "0 auto", padding: "24px 20px" },
  section: {
    background: "#fff", borderRadius: 16,
    boxShadow: "0 2px 16px rgba(229,62,62,0.07)",
    marginBottom: 20, overflow: "hidden",
  },
  sectionTitle: {
    margin: 0, padding: "16px 24px",
    fontSize: 14, fontWeight: 700, color: "#1a1a2e",
    borderBottom: "1px solid #fff5f5",
    background: "linear-gradient(90deg,#fff5f5,#fff)",
    letterSpacing: 0.3,
  },
  sectionBody: { padding: "20px 24px" },
  hint: { margin: "0 0 16px", fontSize: 13, color: "#9ca3af" },

  pantallaGrid: { display: "flex", gap: 14, flexWrap: "wrap" },
  pantallaBtn: {
    flex: 1, minWidth: 140,
    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
    padding: "20px 16px", border: "2px solid #e5e7eb",
    borderRadius: 14, background: "#f9fafb", cursor: "pointer",
    position: "relative", transition: "all .2s",
  },
  pantallaBtnActive: {
    border: "2px solid #e53e3e", background: "#fff5f5",
    boxShadow: "0 0 0 4px rgba(229,62,62,0.10)",
  },
  pantallaBtnLabel: { fontSize: 14, fontWeight: 600, color: "#1a1a2e" },
  pantallaBtnCheck: {
    position: "absolute", top: 8, right: 10,
    background: "#e53e3e", color: "#fff",
    borderRadius: "50%", width: 20, height: 20,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 11, fontWeight: 700,
  },

  modulosGrid: { display: "flex", flexDirection: "column", gap: 8 },
  moduloCard: {
    display: "flex", alignItems: "center", gap: 14,
    padding: "12px 16px", borderRadius: 12,
    border: "1.5px solid #e5e7eb", cursor: "pointer",
    transition: "all .2s", background: "#fafafa",
  },
  moduloCardActive: { background: "#fff5f5", border: "1.5px solid #fca5a5" },
  moduloIcon: { fontSize: 24, flexShrink: 0, width: 32, textAlign: "center" },
  moduloInfo: { flex: 1, display: "flex", flexDirection: "column", gap: 2 },
  moduloLabel: { fontSize: 14, fontWeight: 600, color: "#1a1a2e" },
  moduloDesc:  { fontSize: 12, color: "#9ca3af" },

  zonasGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(140px,1fr))",
    gap: 10,
  },
  zonaBtn: {
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: 6, padding: "14px 8px",
    border: "1.5px solid #e5e7eb", borderRadius: 12,
    background: "#fafafa", cursor: "pointer",
    position: "relative", transition: "all .2s",
  },
  zonaBtnActive: {
    border: "1.5px solid #e53e3e", background: "#fff5f5",
    boxShadow: "0 0 0 3px rgba(229,62,62,0.10)",
  },
  zonaBtnLabel: { fontSize: 12, fontWeight: 500, color: "#374151", textAlign: "center" },
  zonaCheck: {
    position: "absolute", top: 6, right: 6,
    background: "#e53e3e", color: "#fff",
    borderRadius: "50%", width: 18, height: 18,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 10, fontWeight: 700,
  },

  toggleBtn: {
    width: 44, height: 24, borderRadius: 12, border: "none",
    cursor: "pointer", position: "relative", transition: "background .25s",
    flexShrink: 0,
  },
  toggleKnob: {
    position: "absolute", top: 2, width: 20, height: 20,
    background: "#fff", borderRadius: "50%",
    transition: "transform .25s", boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
  },

  meta: {
    display: "flex", gap: 24, flexWrap: "wrap",
    fontSize: 12, color: "#9ca3af", marginBottom: 16, padding: "0 4px",
  },
  btn: {
    width: "100%", padding: "15px",
    background: "linear-gradient(135deg, #c53030, #e53e3e)",
    color: "#fff", border: "none", borderRadius: 12,
    fontSize: 15, fontWeight: 700, cursor: "pointer",
    boxShadow: "0 4px 20px rgba(229,62,62,0.3)", transition: "opacity .2s",
  },
  empty: { textAlign: "center", padding: 60, color: "#9ca3af", fontSize: 16 },
};

export default ConfiguracionModulos;